import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Floor, Scale, Subtract, Total } from "@pdfking/math"
import { NullQ } from "@pdfking/structs"

import type { CalendarArea } from "#internal"

import { AgendaDate } from "../agenda/AgendaDate"
import { AgendaWeeks } from "../agenda/AgendaWeeks"
import { WeekdayName } from "../agenda/WeekdayName"
import { Slice } from "../layout/Slice"
import { Area } from "./Area"
import { DayColor } from "./DayColor"

// The share of the last column Saturday takes, Sunday ruled under it
const SATURDAY = 0.6

/** Week `week` of `year` across `box` as a desk planner has it, its days
 * `gap` apart: Monday to Saturday a column each, Sunday under Saturday;
 * each its number and its name on its first line — the days of the years
 * either side muted, a holiday's in its color (see DayColor) —, or its name
 * alone with no `week`, then lines `pitch` apart; the names in `language`. */
export const PlannerWeek = (
  box: SpatialRectangle<Distance>,
  year: number,
  week: number | undefined,
  pitch: Distance,
  gap: Distance,
  language: string
): CalendarArea[] => {
  const columns = Slice.grid(box, 6, 1, gap)
  const [saturday, sunday] = Slice.top(
    columns[5]!,
    Scale(pitch, Floor((SATURDAY * columns[5]!.height) / pitch))
  )
  const font = Scale(pitch, 0.45)
  const { first } = AgendaWeeks(year)
  return [...columns.slice(0, 5), saturday, sunday].flatMap((day, weekday) => {
    const date = NullQ(week) ? undefined : AgendaDate(year, first + 7 * (week - 1) + weekday)
    const color = DayColor(weekday, !NullQ(date) && date.year !== year)
    // The first line's text, mid line
    const [line] = Slice.top(day, pitch)
    const label = { ...line, height: font, y: Total(line.y, Scale(Subtract(pitch, font), 0.5)) }
    const name = WeekdayName(weekday, language).toUpperCase()
    return [
      ...(date
        ? [
            Area.text({ ...label, width: Scale(font, 1.6) }, `${date.day}`, color),
            Area.text(Slice.after(label, Total(label.x, Scale(font, 1.6))), name, color),
          ]
        : [Area.text(label, name, color)]),
      Area.rules(day, pitch),
    ]
  })
}
